/**
 * AgenticPaymentReferral - Shared Utilities
 *
 * Pure functions for badge styling, date formatting, and action resolution.
 */

import { REFERRAL_STATUSES } from './constants';

/**
 * Returns inline style object for a referral status badge.
 * This function is pure and idempotent, same input always yields same output.
 *
 * @param {string} status - One of the REFERRAL_STATUSES values
 * @returns {{ background: string, color: string }}
 */
export const getStatusBadgeStyle = (status) => {
  const styles = {
    [REFERRAL_STATUSES.AWAITING_PAYMENT]: { background: '#fff3cd', color: '#856404' },
    [REFERRAL_STATUSES.PAID]:             { background: '#d4edda', color: '#155724' },
    [REFERRAL_STATUSES.HELD]:             { background: '#ffeeba', color: '#7d5a00' },
    [REFERRAL_STATUSES.REJECTED]:         { background: '#f8d7da', color: '#721c24' },
    [REFERRAL_STATUSES.NOT_ELIGIBLE]:     { background: '#e2e3e5', color: '#383d41' },
  };
  return styles[status] || { background: '#e2e3e5', color: '#383d41' };
};

/**
 * Returns the list of action names applicable for a given referral status.
 *
 * @param {string} status
 * @returns {string[]}
 */
export const getActionButtons = (status) => {
  switch (status) {
    case REFERRAL_STATUSES.AWAITING_PAYMENT:
      return ['pay', 'hold', 'reject'];
    case REFERRAL_STATUSES.HELD:
      return ['release', 'reject'];
    default:
      return [];
  }
};

/**
 * Format an ISO date string to a locale-aware display string.
 *
 * @param {string|null} isoString
 * @returns {string}
 */
export const formatReferralDate = (isoString) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString();
};

/**
 * Calculate pagination page range for display.
 *
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {number} showPages
 * @returns {number[]}
 */
export const calculatePaginationRange = (currentPage, totalPages, showPages = 5) => {
  const pages = [];
  let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
  let endPage = Math.min(totalPages, startPage + showPages - 1);
  if (endPage - startPage + 1 < showPages) {
    startPage = Math.max(1, endPage - showPages + 1);
  }
  for (let i = startPage; i <= endPage; i++) pages.push(i);
  return pages;
};
